import { formatHours } from '@/features/time-study/presentation';
import { plural } from '@/lib/plural';

import type { Participant, ReworkEvent } from './types';

// Reprocesos en palabras (spec 2026-10-14 §5 D3; so=380): etapas, roles y atribución.
// Cero enums crudos en la UI (UX-COPY-01).

const STAGE: Record<string, string> = {
  borrador: 'Elaboración',
  en_revision: 'Revisión',
  en_aprobacion: 'Aprobación',
  vigente: 'Vigente',
};

const ROLE: Record<ReworkEvent['attributedRole'], string> = {
  elaborador: 'Elaborador',
  revisor: 'Revisor',
  aprobador: 'Aprobador',
  capacitador: 'Capacitador',
};

const stage = (status: string): string => STAGE[status] ?? 'Otra etapa';

/** "Revisión → Elaboración": de qué etapa volvió y a cuál. */
export function transitionText(e: Pick<ReworkEvent, 'fromStatus' | 'toStatus'>): string {
  return `${stage(e.fromStatus)} → ${stage(e.toStatus)}`;
}

/** "Atribuido a X (rol, regla)" o "Sin atribuir". */
export function attributionText(e: ReworkEvent): string {
  if (e.attributionSource === 'sin_atribuir' || e.originatorUserId === null) {
    return 'Sin atribuir';
  }
  const who = e.originatorName ?? 'Usuario sin nombre';
  const rule = e.attributionSource === 'mayor_esfuerzo' ? ', por mayor esfuerzo' : '';
  return `Atribuido a ${who} (${ROLE[e.attributedRole]}${rule})`;
}

/** Horas de reproceso: la unión de tramos activos (un solapado no cuenta dos veces). */
export function reworkHours(e: ReworkEvent): string {
  return formatHours(e.activeUnionSeconds);
}

/**
 * Conteo por persona y rol del documento, incluidos los que no tuvieron reprocesos. La
 * clave es (persona, rol): "Sin atribuir" puede venir en varios roles y dos personas
 * pueden llamarse igual.
 */
export function participantsText(list: Participant[]): { key: string; text: string }[] {
  return list.map((p) => {
    const count =
      p.reworkCount === 0 ? 'sin reprocesos' : plural(p.reworkCount, 'reproceso', 'reprocesos');
    const who = p.userId === null ? 'Sin atribuir' : (p.name ?? 'Usuario sin nombre');
    return { key: `${p.userId ?? 'none'}:${p.role}`, text: `${who} (${ROLE[p.role]}): ${count}` };
  });
}

/** Devoluciones del documento: conteo exacto aunque el detalle venga recortado. */
export function reworkTotal(list: Participant[]): number {
  return list.reduce((sum, p) => sum + p.reworkCount, 0);
}
